import { Toaster } from "@/components/ui/sonner"
import { Toast } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthProvider } from "@/contexts/AuthContext"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { Navigation } from "@/components/Navigation"
import ErrorBoundary from "@/components/ErrorBoundary"
import { useAuth } from "@/contexts/AuthContext"
import { useEffect } from "react"
import { showError } from "@/utils/toast"
import Index from "@/pages/Index"
import NotFound from "@/pages/NotFound"
import Login from "@/pages/Login"
import SignUp from "@/pages/SignUp"
import ForgotPassword from "@/pages/ForgotPassword"
import ResetPassword from "@/pages/ResetPassword"
import Dashboard from "@/pages/Dashboard"
import Home from "@/pages/Home"
import About from "@/pages/About"
import Contact from "@/pages/Contact"
import Terms from "@/pages/Terms"
import Privacy from "@/pages/Privacy"
import Profile from "@/pages/Profile"
import Tasks from "@/pages/Tasks"
import Projects from "@/pages/Projects"
import Calendar from "@/pages/Calendar"
import Documents from "@/pages/Documents"
import TimeTracking from "@/pages/TimeTracking"
import Invoices from "@/pages/Invoices"
import Quotes from "@/pages/Quotes"
import Clients from "@/pages/Clients"
import Contacts from "@/pages/Contacts"
import Messages from "@/pages/Messages"
import Notifications from "@/pages/Notifications"
import Billing from "@/pages/Billing"
import Analytics from "@/pages/Analytics"
import Support from "@/pages/Support"
import AuthCallback from "@/pages/AuthCallback"
import Logout from "@/pages/Logout"
import Onboarding from "@/pages/Onboarding"
import ConnectionTest from "@/pages/ConnectionTest"
import DebugPermissions from "@/pages/DebugPermissions"
import Resources from "@/pages/Resources"
import Pricing from "@/pages/Pricing"
import ClientPortal from "@/pages/ClientPortal"
import Admin from "@/pages/Admin"
import AdminDashboard from "@/pages/admin/AdminDashboard"
import UserManagement from "@/pages/admin/UserManagement"
import CompanyManagement from "@/pages/admin/CompanyManagement"
import Settings from "@/pages/admin/Settings"
import GodMode from "@/pages/admin/GodMode"
import BillingAdmin from "@/pages/admin/Billing"
import AnalyticsAdmin from "@/pages/admin/Analytics"
import DNSManagement from "@/pages/admin/DNSManagement"
import "./globals.css"
import DataProvider from "@/components/DataProvider"
import PublicLayoutWrapper from "@/components/PublicLayoutWrapper"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

// Separate component for auth-protected layout
const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()
  
  useEffect(() => {
    if (!isLoading && !user) {
      showError("Please log in to access this page")
      window.location.href = "/login"
    }
  }, [user, isLoading])
  
  if (isLoading || !user) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>
  }
  
  return (
    <div className="flex h-screen bg-background">
      <Navigation />
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  )
}

// Wrapper component for protected routes
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <AuthLayout>
      {children}
    </AuthLayout>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DataProvider>
              <TooltipProvider>
                <div className="min-h-screen bg-background">
                  <Toast />
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<PublicLayoutWrapper><Index /></PublicLayoutWrapper>} />
                    <Route path="/home" element={<PublicLayoutWrapper><Home /></PublicLayoutWrapper>} />
                    <Route path="/about" element={<PublicLayoutWrapper><About /></PublicLayoutWrapper>} />
                    <Route path="/contact" element={<PublicLayoutWrapper><Contact /></PublicLayoutWrapper>} />
                    <Route path="/terms" element={<PublicLayoutWrapper><Terms /></PublicLayoutWrapper>} />
                    <Route path="/privacy" element={<PublicLayoutWrapper><Privacy /></PublicLayoutWrapper>} />
                    <Route path="/pricing" element={<PublicLayoutWrapper><Pricing /></PublicLayoutWrapper>} />
                    <Route path="/resources" element={<PublicLayoutWrapper><Resources /></PublicLayoutWrapper>} />
                    <Route path="/login" element={<PublicLayoutWrapper><Login /></PublicLayoutWrapper>} />
                    <Route path="/signup" element={<PublicLayoutWrapper><SignUp /></PublicLayoutWrapper>} />
                    <Route path="/forgot-password" element={<PublicLayoutWrapper><ForgotPassword /></PublicLayoutWrapper>} />
                    <Route path="/reset-password" element={<PublicLayoutWrapper><ResetPassword /></PublicLayoutWrapper>} />
                    <Route path="/auth/callback" element={<PublicLayoutWrapper><AuthCallback /></PublicLayoutWrapper>} />
                    <Route path="/logout" element={<PublicLayoutWrapper><Logout /></PublicLayoutWrapper>} />
                    <Route path="/connection-test" element={<PublicLayoutWrapper><ConnectionTest /></PublicLayoutWrapper>} />
                    
                    {/* Protected routes */}
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/dashboard/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/dashboard/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
                    <Route path="/dashboard/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
                    <Route path="/dashboard/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
                    <Route path="/dashboard/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                    <Route path="/dashboard/time-tracking" element={<ProtectedRoute><TimeTracking /></ProtectedRoute>} />
                    <Route path="/dashboard/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
                    <Route path="/dashboard/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
                    <Route path="/dashboard/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
                    <Route path="/dashboard/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
                    <Route path="/dashboard/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/dashboard/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/dashboard/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                    <Route path="/dashboard/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                    <Route path="/dashboard/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
                    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    <Route path="/debug-permissions" element={<ProtectedRoute><DebugPermissions /></ProtectedRoute>} />
                    
                    {/* Client portal routes */}
                    <Route path="/client-portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
                    <Route path="/client-portal/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/client-portal/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    
                    {/* Admin routes */}
                    <Route path="/dashboard/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/companies" element={<ProtectedRoute><CompanyManagement /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/godmode" element={<ProtectedRoute><GodMode /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/billing" element={<ProtectedRoute><BillingAdmin /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/analytics" element={<ProtectedRoute><AnalyticsAdmin /></ProtectedRoute>} />
                    <Route path="/dashboard/admin/dns" element={<ProtectedRoute><DNSManagement /></ProtectedRoute>} />
                    
                    {/* Catch all */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </TooltipProvider>
            </DataProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App